import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  // Estado deseado del drawer según el usuario: true entre un open "oficial"
  // (DialogOpenEvent) y un close "oficial" (DialogCloseEvent). Si un morph
  // destruye/cierra el <dialog> por fuera, este flag permite restaurarlo.
  #shouldBeOpen = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#trackOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#trackClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#trackOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#trackClose);
  }

  #trackOpen = () => {
    this.#shouldBeOpen = true;
  };

  #trackClose = () => {
    this.#shouldBeOpen = false;
  };

  #handleCartUpdate = (event) => {
    // GOTCHA Horizon: CartAddEvent.eventName === 'cart:update', el mismo evento
    // que disparan los cambios de cantidad, gift-reward y apps de terceros.
    // auto-open solo debe reaccionar a AÑADIDOS reales (CartAddEvent de un
    // product form); si no, cualquier refresco del carrito abría el drawer solo.
    if (event instanceof CartAddEvent && this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    // Todo cart:update puede acabar en un morph de la sección del header que
    // detache o reemplace el <dialog> abierto (pierde el estado modal del top
    // layer): drawer "comprimido" o cerrado sin pasar por closeDialog(), y el
    // scroll de la página bloqueado. Verificación + reparación tras el morph.
    this.#scheduleSelfHeal();
  };

  #scheduleSelfHeal() {
    requestAnimationFrame(() => this.#selfHeal());
    setTimeout(() => this.#selfHeal(), 200);
    setTimeout(() => this.#selfHeal(), 600);
  }

  #selfHeal() {
    // refs se re-sincroniza solo (MutationObserver de Component), pero a mitad
    // de un morph puede apuntar a un nodo aún no re-insertado: esperar al
    // siguiente disparo programado en vez de operar sobre un nodo suelto.
    const dialog = this.refs.dialog;
    if (!dialog || !dialog.isConnected) return;

    let isModal = false;
    try {
      isModal = dialog.matches(':modal');
    } catch (e) {}

    if (dialog.open && !isModal) {
      // Abierto pero fuera del top layer (morph lo detachó): re-abrir como modal.
      try {
        dialog.close();
      } catch (e) {}
      this.showDialog();
      return;
    }

    if (!dialog.open && this.#shouldBeOpen) {
      // El usuario no lo cerró; lo cerró un morph (p. ej. reemplazó el nodo).
      // Restaurar el drawer. showDialog() re-aplica el scroll-lock coherente.
      this.showDialog();
    }
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
